<script setup>
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";

useBreadcrumbs().setBreadcrumb("Super User");
</script>

<template>
  <div class="space-y-6">
    <div class="box">
      <p class="text-xs uppercase tracking-[0.2em] text-slate-400">
        Super User
      </p>
      <h1 class="text-2xl font-semibold text-slate-900 dark:text-slate-100">
        Control Centre
      </h1>
      <p class="mt-2 text-sm text-slate-500">Reserved for advanced tools.</p>
    </div>

    <div class="grid gap-4 md:grid-cols-2">
      <router-link
        to="/super/team-performance"
        class="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
      >
        <h2
          class="mt-2 text-lg font-semibold text-slate-900 group-hover:text-cyan-700 dark:text-slate-100 dark:group-hover:text-cyan-300"
        >
          Team Performance
        </h2>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-300">
          Review customer work, response times, ticket outcomes, and quote results.
        </p>
      </router-link>
      <router-link
        to="/super/users"
        class="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
      >
        <h2
          class="mt-2 text-lg font-semibold text-slate-900 group-hover:text-cyan-700 dark:text-slate-100 dark:group-hover:text-cyan-300"
        >
          User Management
        </h2>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-300">
          Send password reset emails, toggle access, and manage roles.
        </p>
      </router-link>
      <router-link
        to="/super/ticket-notifications"
        class="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
      >
        <h2
          class="mt-2 text-lg font-semibold text-slate-900 group-hover:text-violet-700 dark:text-slate-100 dark:group-hover:text-violet-300"
        >
          Ticket Notifications
        </h2>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-300">
          Manage sender exclusions for the Slack tickets channel.
        </p>
      </router-link>
      <router-link
        to="/super/units"
        class="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
      >
        <h2
          class="mt-2 text-lg font-semibold text-slate-900 group-hover:text-blue-700 dark:text-slate-100 dark:group-hover:text-blue-300"
        >
          Unit Management
        </h2>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-300">
          Create units and manage their vehicle and personnel requirements.
        </p>
      </router-link>
      <router-link
        to="/super/invoice-automation"
        class="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
      >
        <h2
          class="mt-2 text-lg font-semibold text-slate-900 group-hover:text-emerald-700 dark:text-slate-100 dark:group-hover:text-emerald-300"
        >
          Invoice Automation
        </h2>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-300">
          Review automatic invoice sends, reminders, and upcoming schedules.
        </p>
      </router-link>
      <router-link
        to="/super/stripe-invoices"
        class="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
      >
        <h2
          class="mt-2 text-lg font-semibold text-slate-900 group-hover:text-indigo-700 dark:text-slate-100 dark:group-hover:text-indigo-300"
        >
          Stripe Reconciliation
        </h2>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-300">
          Match gross Stripe payments to invoices and reconcile net payouts to
          the bank.
        </p>
      </router-link>
      <router-link
        to="/super/quote-automation"
        class="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
      >
        <h2
          class="mt-2 text-lg font-semibold text-slate-900 group-hover:text-cyan-700 dark:text-slate-100 dark:group-hover:text-cyan-300"
        >
          Quotation Automation
        </h2>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-300">
          Review quote chasing, event follow-ups, audit history, and bank
          holidays.
        </p>
      </router-link>
      <router-link
        to="/super/companies-house-queries"
        class="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
      >
        <h2
          class="mt-2 text-lg font-semibold text-slate-900 group-hover:text-cyan-700 dark:text-slate-100 dark:group-hover:text-cyan-300"
        >
          Companies House Queries
        </h2>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-300">
          Audit scheduled checks, document requests, failures, and response
          times.
        </p>
      </router-link>
      <router-link
        to="/super/activity"
        class="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
      >
        <h2
          class="mt-2 text-lg font-semibold text-slate-900 group-hover:text-amber-700 dark:text-slate-100 dark:group-hover:text-amber-300"
        >
          Activity Feed
        </h2>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-300">
          See recent writes and updates across the platform.
        </p>
      </router-link>
      <router-link
        to="/super/page-views"
        class="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
      >
        <h2
          class="mt-2 text-lg font-semibold text-slate-900 group-hover:text-amber-700 dark:text-slate-100 dark:group-hover:text-amber-300"
        >
          Page Views
        </h2>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-300">
          See recent writes and updates across the platform.
        </p>
      </router-link>
    </div>
  </div>
</template>
